import React, { useCallback, useMemo } from 'react'
import classNames from 'classnames'
import { Else, If, Then } from 'react-if'
import { StarFilledIcon } from '@/atoms/Icons/StarFilledIcon'
import { LabelSM } from '@/atoms/Text'
import { PIF_NAME_IN_CREDITS_MIN_TICKETS } from '@/constants/pif'
import { usePayItForwardOfferData } from '@/contexts/PayItForwardOffersDataContext'
import { Offer } from '@/services/PifService'
import { getPeopleImpacted } from '@/services/PifService/PifService'
import { formatPrice } from '@/services/PriceFormatter'
import { useLocale } from '@/utils/LocaleUtil'
import { useTranslate } from '@/utils/translate/translate-client'

const nameInCreditsBorderGradient = 'linear-gradient(159deg, #DFB695 14%, #F3D0B6 41%, #DEA67D 62%, #B98268 89%)'

// Helper component to render Caption with Name-in-Credits styles (new PIF layout only)
const NameInCreditsChip = ({
  washingtonOfferExperiment,
  label,
  isSelected,
  isEligible,
}: {
  label: string
  isSelected: boolean
  isEligible: boolean
  washingtonOfferExperiment?: boolean
}) => {
  const caption = (
    <LabelSM
      weight="semibold"
      className={classNames(
        'rounded-[50px] border-[1.5px] relative text-xs md:text-sm md:px-[18px] py-[10px] px-4 flex items-center justify-center select-none',
        {
          'border-copper border-[1.5px] !text-core-gray-800 bg-transparent': isSelected && isEligible,
          '!bg-core-gray-950 !border-core-gray-950 !text-white': isSelected && !isEligible,
          '!bg-core-gray-300 !border-core-gray-400 !text-core-gray-800': !isSelected,
        },
        washingtonOfferExperiment && '!py-[7px] !px-[18px] h-10 !text-[.9375rem]',
      )}
    >
      {isEligible && !isSelected && (
        <span className="pointer-events-none absolute -right-1.5 -top-1 z-10 mr-1 flex h-4 w-4 items-center justify-center rounded-full border-[2px] border-white bg-warm-03">
          <StarFilledIcon size={10} color={'white'} />
        </span>
      )}
      {label}
    </LabelSM>
  )

  return isEligible && isSelected ? (
    <div className="rounded-full p-[1px]" style={{ backgroundImage: nameInCreditsBorderGradient }}>
      {caption}
    </div>
  ) : (
    caption
  )
}

interface TheatricalExpressPriceOptionsProps {
  offers: Offer[]
  onClick: (offer: Offer) => void
  selectedOffer: Offer | undefined
  shouldShowPeopleImpacted?: boolean
  shouldUseEqualColumns?: boolean
  pifNameInCreditsEnabled?: boolean
  washingtonOfferExperiment?: boolean
}

export const TheatricalExpressPriceOptions: React.FC<TheatricalExpressPriceOptionsProps> = ({
  offers,
  onClick,
  selectedOffer,
  shouldShowPeopleImpacted = true,
  shouldUseEqualColumns = false,
  pifNameInCreditsEnabled = false,
  washingtonOfferExperiment = false,
}) => {
  const { locale } = useLocale()
  const { setNameInCredits } = usePayItForwardOfferData()
  const { t } = useTranslate('pif')

  const getReach = useCallback(
    (offer: Offer) => {
      const num = getPeopleImpacted(offer)
      return new Intl.NumberFormat(locale, { notation: 'compact' }).format(num)
    },
    [locale],
  )

  const filteredOffers = useMemo(() => {
    if (washingtonOfferExperiment) {
      const allowedPrices = new Set([1500, 7500, 15000, 50000, 150000, 750000])
      return offers?.filter((offer) => allowedPrices.has(offer.price)) || []
    }
    return offers?.filter((offer) => offer.price <= 750000) || []
  }, [offers, washingtonOfferExperiment])

  const handleOptionClick = useCallback(
    (offer: Offer) => {
      const ticketCount = getPeopleImpacted(offer)
      if (ticketCount < PIF_NAME_IN_CREDITS_MIN_TICKETS) {
        setNameInCredits('')
      }
      onClick(offer)
    },
    [onClick, setNameInCredits],
  )

  const label = useCallback(
    (offer: Offer, price: string) => {
      if (washingtonOfferExperiment) {
        switch (offer.price) {
          case 1500:
            return t('sendAStudent', '{{price}} - Send a Student', { price })
          case 7500:
            return t('sendFiveStudent', '{{price}} - Send 5 Students', { price })
          case 15000:
            return t('sendTenStudent', '{{price}} - Send 10 Students', { price })
          case 50000:
            return t('sendAClass', '{{price}} - Send a Class', { price })
          case 150000:
            return t('sendAGrade', '{{price}} - Send a Grade', { price })
          case 750000:
            return t('sendASchool', '{{price}} - Send a School', { price })
          default:
            return price
        }
      }
      return shouldShowPeopleImpacted ? getReach(offer) : price
    },
    [shouldShowPeopleImpacted, getReach, washingtonOfferExperiment, t],
  )

  return (
    <>
      <If condition={shouldUseEqualColumns}>
        <Then>
          <div
            className={classNames(
              'mt-4 grid grid-cols-5 gap-2 md:mt-6',
              washingtonOfferExperiment && '!grid-cols-1 md:!grid-cols-2',
            )}
          >
            {filteredOffers.map((offer) => {
              const price = formatPrice(offer.price, { includeDecimals: false, currency: offer.currency })
              const isEligible = getPeopleImpacted(offer) >= PIF_NAME_IN_CREDITS_MIN_TICKETS
              const isSelected = offer.id === selectedOffer?.id
              const labelText = label(offer, price)
              return (
                <div
                  key={offer?.id}
                  className="flex-basis-auto shrink-0 grow cursor-pointer text-center"
                  onClick={() => handleOptionClick(offer)}
                >
                  <NameInCreditsChip
                    washingtonOfferExperiment={washingtonOfferExperiment}
                    label={labelText}
                    isSelected={isSelected}
                    isEligible={pifNameInCreditsEnabled && isEligible}
                  />
                </div>
              )
            })}
          </div>
        </Then>
        <Else>
          <div className="mt-6 flex flex-wrap gap-1">
            {filteredOffers.map((offer) => {
              const price = formatPrice(offer.price, { includeDecimals: false, currency: offer.currency })
              const isEligible = getPeopleImpacted(offer) >= PIF_NAME_IN_CREDITS_MIN_TICKETS
              const isSelected = offer.id === selectedOffer?.id
              const labelText = label(offer, price)
              return (
                <div
                  key={offer?.id}
                  className="flex-basis-auto shrink-0 grow cursor-pointer text-center"
                  onClick={() => handleOptionClick(offer)}
                >
                  <NameInCreditsChip
                    washingtonOfferExperiment={washingtonOfferExperiment}
                    label={labelText}
                    isSelected={isSelected}
                    isEligible={pifNameInCreditsEnabled && isEligible}
                  />
                </div>
              )
            })}
          </div>
        </Else>
      </If>
    </>
  )
}
